import { Component, inject, signal, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { 
  AccessPlan, 
  ProductPackage, 
  ProductPackageControllerService,
  AccessPlanControllerService,
  PlanUpgradeRequestDTO,
  PlanUpgradeResponseDTO
} from '../../../../core/generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { modalFadeIn } from '../../shared/animations';
import { PlanUpgradeConfirmationComponent } from '../plan-upgrade-confirmation/plan-upgrade-confirmation.component';

/**
 * PlanUpgradeModalComponent
 * Modal para seleção de plano superior para upgrade
 */
@Component({
  selector: 'app-plan-upgrade-modal',
  standalone: true,
  imports: [MaterialModule, CommonModule],
  templateUrl: './plan-upgrade-modal.component.html',
  styleUrl: './plan-upgrade-modal.component.scss',
  animations: [modalFadeIn]
})
export class PlanUpgradeModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<PlanUpgradeModalComponent>);
  data = inject<{ plan: AccessPlan }>(MAT_DIALOG_DATA);
  dialog = inject(MatDialog);

  private packageService = inject(ProductPackageControllerService);
  private accessPlanService = inject(AccessPlanControllerService);
  private snackBar = inject(MatSnackBar);

  availablePackages = signal<ProductPackage[]>([]);
  isLoading = signal(true);
  selectedPackage = signal<ProductPackage | null>(null);

  ngOnInit(): void {
    this.loadAvailablePackages();
  }

  /**
   * Carrega pacotes disponíveis para upgrade
   */
  loadAvailablePackages(): void {
    this.isLoading.set(true);
    
    this.packageService.findAll5(0, 100, 'name').subscribe({
      next: (response) => {
        const currentPlanValue = this.data.plan.finalTotal || 0;
        
        // Filtrar apenas pacotes com valor maior que o plano atual e ativos
        const eligiblePackages = (response.content || []).filter((pkg: ProductPackage) => {
          const packageTotal = this.calculatePackageTotal(pkg);
          return packageTotal > currentPlanValue && pkg.active;
        });
        
        this.availablePackages.set(eligiblePackages);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Erro ao carregar pacotes:', error);
        this.snackBar.open('Erro ao carregar planos disponíveis', 'Fechar', { duration: 4000 });
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Calcula o valor total de um pacote
   */
  calculatePackageTotal(pkg: ProductPackage): number {
    if (!pkg.items) return 0;
    
    const itemsTotal = pkg.items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.unitPrice || 0));
    }, 0);
    
    const discountsTotal = (pkg.discounts || []).reduce((sum, discount) => {
      return sum + ('value' in discount && typeof discount.value === 'number' ? discount.value : 0);
    }, 0);
    
    const percentageDiscountAmount = 0; // Assuming no percentage discounts are applicable
    
    return itemsTotal - discountsTotal - percentageDiscountAmount;
  }

  /**
   * Seleciona um pacote
   */
  selectPackage(pkg: ProductPackage): void {
    this.selectedPackage.set(pkg);
  }

  /**
   * Confirma a seleção e abre modal de confirmação
   */
  confirmSelection(): void {
    const selected = this.selectedPackage();
    if (!selected || !selected.id) return;

    // Fechar este modal e abrir o de confirmação
    this.dialogRef.close();
    
    this.dialog.open(PlanUpgradeConfirmationComponent, {
      width: '700px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        currentPlan: this.data.plan,
        targetPackage: selected
      }
    });
  }

  /**
   * Formata valor em moeda brasileira
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Fecha o modal
   */
  close(): void {
    this.dialogRef.close();
  }
}
